import React, { useState } from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { API_BASE_URL } from '../../config/api';
import './ContactUsPage.css';

const ContactUsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Submit to contact form
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Also create a support ticket so admin can see and reply
        try {
          await fetch(`${API_BASE_URL}/support/tickets`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userName: formData.name,
              userEmail: formData.email,
              subject: formData.subject,
              message: formData.message,
              category: 'General',
              priority: 'Medium'
            })
          });
        } catch (ticketErr) {
          console.error('Support ticket creation error:', ticketErr);
        }

        setSuccess(true);
        setFormData({
          name: '',
          address: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setError(data.error || 'Failed to submit. Please try again.');
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-us-page">
      <Header />
      
      <main className="contact-main">
        <div className="contact-container">
          {/* Page Header */}
          <div className="contact-header">
            <h1 className="contact-title">Contact us</h1>
            <p className="contact-subtitle">
              Get in touch with us for support, inquiries, or any opportunities.
            </p>
          </div>

          {/* Contact Content */}
          <div className="contact-content">
            {/* Left Column - Contact Info */}
            <div className="contact-info-section">
              <div className="contact-info-header">
                <h2 className="contact-info-title">Contact Us</h2>
                <p className="contact-info-description">
                  Feel free to reach out via the form below, or contact us directly through email or phone. We look forward to connecting with you and ensuring your next cannoli experience is nothing short of delightful!
                </p>
              </div>

              <div className="contact-details">
                <div className="contact-detail-item">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.4443 2.7041L13.6211 2.71289C14.0309 2.75417 14.4161 2.93577 14.71 3.22949C15.0456 3.56516 15.2353 4.02042 15.2363 4.49512V11.5049C15.2358 11.9798 15.0468 12.4356 14.7109 12.7715C14.417 13.0654 14.0311 13.2461 13.6211 13.2871L13.4443 13.2959H2.55566C2.0808 13.2954 1.6249 13.1072 1.28906 12.7715C0.953216 12.4356 0.764202 11.9798 0.763672 11.5049V4.49512L0.772461 4.31836C0.813775 3.90857 0.995288 3.52327 1.28906 3.22949C1.62476 2.8938 2.07996 2.70498 2.55469 2.7041H13.4443ZM9.34473 8.94043C8.53937 9.5566 7.46275 9.55637 6.6543 8.94043L2.50195 12.374C2.51974 12.3753 2.53768 12.3799 2.55566 12.3799H13.4434C13.4611 12.3797 13.4785 12.3753 13.4961 12.374L9.34473 8.94043ZM1.71387 4.2666C1.69253 4.34008 1.68066 4.41687 1.68066 4.49609V11.5039L1.68848 11.6201C1.69787 11.6869 1.71664 11.7517 1.74121 11.8145L5.9668 8.32031L1.71387 4.2666ZM10.0322 8.32031L14.2568 11.8145C14.2953 11.7174 14.3193 11.614 14.3193 11.5049V4.49609C14.3193 4.41675 14.3055 4.34015 14.2842 4.2666L10.0322 8.32031ZM2.55469 3.62012C2.49589 3.62013 2.43845 3.62611 2.38281 3.6377L7.08887 8.12402C7.33431 8.35786 7.661 8.48828 8 8.48828C8.33884 8.48817 8.66482 8.35776 8.91016 8.12402L13.6133 3.63867C13.5585 3.62749 13.5022 3.6211 13.4443 3.62109L2.55469 3.62012Z" fill="#333333" stroke="#333333" strokeWidth="0.25"/>
                  </svg>
                  <span>Info@gulfconstructor.com</span>
                </div>
                <div className="contact-detail-item">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_contact_whatsapp)">
                      <path fillRule="evenodd" clipRule="evenodd" d="M12.7669 2.17987C11.3636 0.774914 9.49725 0.000820312 7.50898 0C3.41201 0 0.0776562 3.33426 0.0760156 7.43233C0.0754531 8.74237 0.417664 10.0212 1.06815 11.1484L0.0136719 15L3.95401 13.9664C5.03972 14.5586 6.26206 14.8707 7.50596 14.8711H7.5091C11.6056 14.8711 14.9403 11.5366 14.9419 7.43829C14.9427 5.45215 14.1703 3.58476 12.7669 2.17987ZM7.50898 13.6158H7.5064C6.3979 13.6153 5.31071 13.3174 4.36203 12.7546L4.13656 12.6207L1.7983 13.2341L2.42239 10.9543L2.27544 10.7206C1.65701 9.73696 1.33044 8.60008 1.331 7.43278C1.33229 4.02656 4.10377 1.25536 7.51147 1.25536C9.1616 1.2559 10.7128 1.89933 11.8792 3.06708C13.0455 4.23483 13.6875 5.78702 13.687 7.43782C13.6855 10.8443 10.9142 13.6158 7.50898 13.6158ZM10.8977 8.98879C10.712 8.89579 9.79889 8.44664 9.6286 8.38457C9.45849 8.32258 9.33453 8.29171 9.21085 8.47757C9.08698 8.66343 8.7311 9.08182 8.62271 9.20568C8.51431 9.32967 8.4061 9.34521 8.22033 9.25221C8.03459 9.15928 7.43618 8.96308 6.72675 8.33036C6.17468 7.83789 5.80198 7.22972 5.69358 7.04388C5.58537 6.85783 5.69265 6.76701 5.77505 6.66482C5.9761 6.41517 6.17743 6.15342 6.23933 6.02955C6.30129 5.90557 6.27026 5.7971 6.22376 5.70417C6.17743 5.61124 5.80601 4.69708 5.65128 4.32511C5.50041 3.96312 5.34741 4.01198 5.23334 4.00633C5.12513 4.00092 5.00126 3.99982 4.8774 3.99982C4.7536 3.99982 4.55236 4.04622 4.38209 4.23227C4.21189 4.41822 3.73217 4.86747 3.73217 5.78163C3.73217 6.69579 4.39765 7.57889 4.49049 7.70285C4.5833 7.82683 5.80015 9.70272 7.66315 10.5071C8.10626 10.6987 8.45215 10.8128 8.72196 10.8984C9.1669 11.0398 9.57167 11.0198 9.89173 10.972C10.2486 10.9186 10.9905 10.5227 11.1454 10.0889C11.3001 9.65505 11.3001 9.28324 11.2536 9.20568C11.2073 9.12825 11.0834 9.08182 10.8977 8.98879Z" fill="#333333"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_contact_whatsapp">
                        <rect width="16" height="16" fill="white"/>
                      </clipPath>
                    </defs>
                  </svg>
                  <span>009715444586500</span>
                </div>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div className="contact-form-section">
              {success && (
                <div className="contact-success-message">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Thank you for contacting us! We will get back to you soon.</span>
                </div>
              )}
              
              {error && (
                <div className="contact-error-message">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-input"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="form-input"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">E-mail</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="form-input"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-textarea"
                    rows="5"
                    required
                    disabled={loading}
                  />
                </div>

                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactUsPage;
